<script setup lang="ts">
import { sampleSize } from 'es-toolkit'
import Broccoli from '~/components/svg/shapes/Broccoli.vue'
import Cucumber from '~/components/svg/shapes/Cucumber.vue'
import Garlic from '~/components/svg/shapes/Garlic.vue'
import Ginger from '~/components/svg/shapes/Ginger.vue'
import Lemon from '~/components/svg/shapes/Lemon.vue'
import Meat from '~/components/svg/shapes/Meat.vue'
import Potato from '~/components/svg/shapes/Potato.vue'
import SweetPepper from '~/components/svg/shapes/SweetPepper.vue'

interface RandomBackgroundImagesProps {
  maxIcons?: number
  isSetAmount?: boolean
  isFixed?: boolean
}

interface ImageStyle {
  styles: {
    top: string
    left: string
    transform: string
    position: 'absolute' | 'fixed'
  }
  isOutline: boolean
}

const props = withDefaults(defineProps<RandomBackgroundImagesProps>(), {
  maxIcons: 3,
  isSetAmount: false,
  isFixed: false
})

const availableImages = [
  Broccoli,
  Cucumber,
  Garlic,
  Ginger,
  Lemon,
  Meat,
  Potato,
  SweetPepper
]

const chosenImages = shallowRef([SweetPepper])

const regenerateImages = () => {
  if (props.isSetAmount) {
    chosenImages.value = sampleSize(availableImages, props.maxIcons)
  } else {
    const imageAmount = Math.ceil(Math.random() * props.maxIcons)
    chosenImages.value = sampleSize(availableImages, imageAmount)
  }
}

defineExpose({
  regenerateImages
})

const minPositionTop = 10
const minPositionLeft = -25
const maxPositionTop = 55
const maxPositionLeft = 55

const locations = [
  {
    top: minPositionTop,
    left: minPositionLeft
  },
  {
    top: minPositionTop,
    left: maxPositionLeft
  },
  {
    top: maxPositionTop,
    left: maxPositionLeft
  },
  {
    top: maxPositionTop,
    left: minPositionLeft
  }
]

const imageArray = computed(() => {
  const tempStyles: ImageStyle[] = []

  const chosenLocations = sampleSize(locations, props.maxIcons)
  let index = 0

  chosenImages.value.forEach((image) => {
    const location = chosenLocations[index]
    index++

    const top = location.top + Math.ceil(-10 + Math.random() * 20)
    const left = location.left + Math.ceil(-10 + Math.random() * 20)

    let isOutline = Math.random() > 0.5

    if (image === Broccoli || image === Cucumber) {
      isOutline = true
    }

    tempStyles.push({
      styles: {
        top: `${top}%`,
        left: `${left}%`,
        transform: `${left < 35 ? 'scaleX(-1)' : 'scaleX(1)'} rotate(${
          -90 + Math.random() * 180
        }deg)`,
        position: props.isFixed ? 'fixed' : 'absolute'
      },
      isOutline: isOutline
    })
  })

  return tempStyles
})

onMounted(() => {
  regenerateImages()
})
</script>

<template>
  <div class="random-background-images overflow-hidden">
    <div v-for="(image, index) in chosenImages" :key="`background-image-${index}`">
      <component :is="image" v-bind="imageArray[index]" />
    </div>
  </div>
</template>

<style scoped></style>
